import { infiniteQueryOptions, queryOptions, type InfiniteData } from "@tanstack/react-query";
import type { WorkspaceCalendarService } from "@/services/workspace-calendar.service";
import type {
  ICalendarSource,
  IHolidayCalendarDatePage,
  THolidayCalendarDateScope,
  IGoogleDomainWideConfig,
  IHolidayCalendar,
  IHolidayCountry,
  IOutlookDomainWideConfig,
} from "@/types/workspace";
import { getQueryClient } from "./client";
import { FRESHNESS } from "./policies";
import { GLOBAL_TENANT, scopedKey, scopedRead, scopeIfReady, type QueryScope } from "./scope";

/**
 * The calendar reads (NG-2814):
 *
 * - `calendarSources.mine`: the signed-in user's own calendar connections,
 *   `['*', uid, 'calendar-sources', 'mine']`. No workspace owns them.
 * - `holidayRegions`: the country and region catalogue the pickers offer,
 *   `['*', uid, 'holiday-regions']`. Reference: the product's list.
 * - `myHolidayCalendar`: the user's own public-holiday region in a workspace,
 *   `[slug, uid, 'my-holiday-calendar']`.
 * - `holidayCalendars.list`: a workspace's admin-managed holiday calendars,
 *   `[slug, uid, 'holiday-calendars', 'list']`.
 * - `holidayCalendars.suggestions`: groups of people no calendar covers,
 *   `[slug, uid, 'holiday-calendars', 'suggestions']`.
 * - `holidayCalendar.detail`: one calendar, `[slug, uid, 'holiday-calendar', id]`.
 * - `holidayCalendar.people`: who one calendar covers,
 *   `[slug, uid, 'holiday-calendar', id, 'people']`.
 * - `holidayCalendar.dates`: one calendar's dates for a filter and sort, read a
 *   page at a time by cursor,
 *   `[slug, uid, 'holiday-calendar', id, 'dates', {scope, search, sort_by, sort_order}]`
 *   (infinite).
 * - `calendarConfig`: a workspace's Google or Outlook domain-wide sync config,
 *   its member statuses read a page at a time by offset,
 *   `[slug, uid, 'calendar-config', provider]` (infinite).
 *
 * Every one is a plain GET, so all declare `"safe-read"`. All but the region
 * catalogue are mutable: they are the settings screens that edit them.
 *
 * @writers `WorkspaceCalendarsStore`: a calendar write patches the list and
 * the calendar's own entry from its answer; create and delete also re-read the
 * suggestions (a calendar covers people, or stops covering them), and a scope
 * edit re-reads the calendar's people. Adding or importing dates, Discard,
 * and connecting, syncing or disconnecting a feed re-read the calendar's
 * dates; the dates panel patches a row it renames, excludes or deletes, and
 * puts it back on failure. Setting or clearing the user's region
 * writes `myHolidayCalendar` and re-reads their sources. A domain-wide config
 * write replaces its entry with the answer and re-reads the user's sources.
 */

/** A domain-wide sync provider. */
export type CalendarProvider = "google" | "outlook";

/** A provider's config, or an empty answer when none is set up. */
type DomainWideConfigPage =
  | IGoogleDomainWideConfig
  | IOutlookDomainWideConfig
  | Record<string, never>;

export function myCalendarSourcesKey(scope: QueryScope) {
  return scopedKey(scope, "calendar-sources", "mine");
}

export function holidayRegionsKey(scope: QueryScope) {
  return scopedKey(scope, "holiday-regions");
}

export function myHolidayCalendarKey(scope: QueryScope) {
  return scopedKey(scope, "my-holiday-calendar");
}

export function holidayCalendarsKey(scope: QueryScope) {
  return scopedKey(scope, "holiday-calendars", "list");
}

export function holidayCalendarSuggestionsKey(scope: QueryScope) {
  return scopedKey(scope, "holiday-calendars", "suggestions");
}

export function holidayCalendarKey(scope: QueryScope, calendarId: string) {
  return scopedKey(scope, "holiday-calendar", calendarId);
}

function holidayCalendarPeopleKey(scope: QueryScope, calendarId: string) {
  return scopedKey(scope, "holiday-calendar", calendarId, "people");
}

/** What a dates read is filtered and sorted by. */
export interface HolidayDatesFilter {
  scope: THolidayCalendarDateScope;
  search: string;
  sort_by: "date" | "name" | "origin";
  sort_order: "asc" | "desc";
}

/** Rows per request. The offset paginator derives its offset from `per_page`, so
 *  every page of one walk must be asked for with the same value — hence a
 *  constant rather than anything the caller can vary mid-walk. */
const DATES_PER_PAGE = 50;

export function holidayCalendarDatesKey(
  scope: QueryScope,
  calendarId: string,
  filter: HolidayDatesFilter,
) {
  return scopedKey(scope, "holiday-calendar", calendarId, "dates", {
    scope: filter.scope,
    search: filter.search,
    sort_by: filter.sort_by,
    sort_order: filter.sort_order,
  });
}

export function calendarConfigKey(scope: QueryScope, provider: CalendarProvider) {
  return scopedKey(scope, "calendar-config", provider);
}

/** The signed-in user's own calendar connections; `scope` is under `GLOBAL_TENANT`. */
export function myCalendarSourcesQuery(
  service: Pick<WorkspaceCalendarService, "listUserSources">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: myCalendarSourcesKey(scope),
    queryFn: scopedRead((request) => service.listUserSources(request), { retry: "safe-read" }),
  });
}

/** The country and region catalogue; `scope` is under `GLOBAL_TENANT`. */
export function holidayRegionsQuery(
  service: Pick<WorkspaceCalendarService, "listHolidayRegions">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: holidayRegionsKey(scope),
    queryFn: scopedRead<IHolidayCountry[]>(
      async (request) => (await service.listHolidayRegions(request))?.countries ?? [],
      { retry: "safe-read" },
    ),
  });
}

/** The user's own public-holiday region in `scope`'s workspace, or `null` when unset. */
export function myHolidayCalendarQuery(
  service: Pick<WorkspaceCalendarService, "getMyHolidayCalendar">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: myHolidayCalendarKey(scope),
    queryFn: scopedRead<ICalendarSource | null>(
      (request) => service.getMyHolidayCalendar(scope.tenant, request),
      { retry: "safe-read" },
    ),
  });
}

/** A workspace's holiday calendars. */
export function holidayCalendarsQuery(
  service: Pick<WorkspaceCalendarService, "listHolidayCalendars">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: holidayCalendarsKey(scope),
    queryFn: scopedRead<IHolidayCalendar[]>(
      async (request) => (await service.listHolidayCalendars(scope.tenant, request)) ?? [],
      { retry: "safe-read" },
    ),
  });
}

/** Groups of people no holiday calendar covers. */
export function holidayCalendarSuggestionsQuery(
  service: Pick<WorkspaceCalendarService, "listHolidayCalendarSuggestions">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: holidayCalendarSuggestionsKey(scope),
    queryFn: scopedRead(
      async (request) =>
        (await service.listHolidayCalendarSuggestions(scope.tenant, request)) ?? [],
      { retry: "safe-read" },
    ),
  });
}

/** One holiday calendar. */
export function holidayCalendarQuery(
  service: Pick<WorkspaceCalendarService, "getHolidayCalendar">,
  scope: QueryScope,
  calendarId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: holidayCalendarKey(scope, calendarId),
    queryFn: scopedRead(
      (request) => service.getHolidayCalendar(scope.tenant, calendarId, request),
      {
        retry: "safe-read",
      },
    ),
  });
}

/** Who one holiday calendar covers. */
export function holidayCalendarPeopleQuery(
  service: Pick<WorkspaceCalendarService, "getHolidayCalendarPeople">,
  scope: QueryScope,
  calendarId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: holidayCalendarPeopleKey(scope, calendarId),
    queryFn: scopedRead(
      (request) => service.getHolidayCalendarPeople(scope.tenant, calendarId, request),
      { retry: "safe-read" },
    ),
  });
}

/**
 * One calendar's dates for `filter`, a page at a time: the first page asks with
 * no cursor, each later one with the cursor the last returned. The server
 * sends a cursor even on the last page, so only a page that says more follow
 * is followed.
 */
export function holidayCalendarDatesQuery(
  service: Pick<WorkspaceCalendarService, "listHolidayCalendarDates">,
  scope: QueryScope,
  calendarId: string,
  filter: HolidayDatesFilter,
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: holidayCalendarDatesKey(scope, calendarId, filter),
    initialPageParam: null as string | null,
    queryFn: scopedRead<IHolidayCalendarDatePage, string | null>(
      (request, { pageParam }) =>
        service.listHolidayCalendarDates(
          scope.tenant,
          calendarId,
          { ...filter, per_page: DATES_PER_PAGE, cursor: pageParam ?? undefined },
          request,
        ),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: IHolidayCalendarDatePage) =>
      last.next_page_results && last.next_cursor ? last.next_cursor : undefined,
  });
}

/**
 * Every date in the loaded pages, once each, in page order: a row can appear
 * on two pages when one was added or removed between reads, and the table keys
 * rows by id.
 */
export function rowsOfDatePages(
  pages: readonly IHolidayCalendarDatePage[],
): IHolidayCalendarDatePage["results"] {
  const seen = new Set<string>();
  const rows: IHolidayCalendarDatePage["results"] = [];
  for (const page of pages) {
    for (const row of page.results) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      rows.push(row);
    }
  }
  return rows;
}

/**
 * A provider's domain-wide config, its member statuses a page at a time: the
 * first page asks with no offset, each later one with the offset the last
 * returned, until there is none.
 */
export function calendarConfigQuery(
  service: Pick<
    WorkspaceCalendarService,
    "getGoogleDomainWideConfig" | "getOutlookDomainWideConfig"
  >,
  scope: QueryScope,
  provider: CalendarProvider,
) {
  const read =
    provider === "google"
      ? service.getGoogleDomainWideConfig.bind(service)
      : service.getOutlookDomainWideConfig.bind(service);
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: calendarConfigKey(scope, provider),
    initialPageParam: undefined as number | undefined,
    queryFn: scopedRead<DomainWideConfigPage, number | undefined>(
      (request, { pageParam }) =>
        read(
          scope.tenant,
          pageParam === undefined ? undefined : { member_offset: pageParam },
          request,
        ),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: DomainWideConfigPage) =>
      "id" in last && last.members_next_offset != null ? last.members_next_offset : undefined,
  });
}

/**
 * The config as one value: the first page's settings, with every page's
 * members in order. `null` when the provider is not set up.
 */
export function configOfPages<T extends IGoogleDomainWideConfig | IOutlookDomainWideConfig>(
  data: InfiniteData<DomainWideConfigPage, unknown> | undefined,
): T | null {
  const pages = data?.pages ?? [];
  const first = pages[0];
  if (!first || !("id" in first)) return null;
  const last = pages[pages.length - 1] as T;
  return {
    ...(first as T),
    members: pages.flatMap((page) => ("id" in page ? (page.members ?? []) : [])),
    members_next_offset: "id" in last ? (last.members_next_offset ?? null) : null,
  };
}

/** Warm a workspace's holiday calendars for a route loader. */
export async function prefetchHolidayCalendars(
  service: Pick<WorkspaceCalendarService, "listHolidayCalendars">,
  slug: string,
): Promise<void> {
  const scope = scopeIfReady(slug, "holiday calendars");
  if (!scope) return;
  await getQueryClient().fetchQuery(holidayCalendarsQuery(service, scope));
}

/** Warm one holiday calendar for its page's loader. */
export async function prefetchHolidayCalendar(
  service: Pick<WorkspaceCalendarService, "getHolidayCalendar">,
  slug: string,
  calendarId: string,
): Promise<void> {
  const scope = scopeIfReady(slug, "holiday calendar");
  if (!scope) return;
  await getQueryClient().fetchQuery(holidayCalendarQuery(service, scope, calendarId));
}

/** Warm the holiday calendar suggestions for the Calendars loader. */
export async function prefetchHolidayCalendarSuggestions(
  service: Pick<WorkspaceCalendarService, "listHolidayCalendarSuggestions">,
  slug: string,
): Promise<void> {
  const scope = scopeIfReady(slug, "holiday calendar suggestions");
  if (!scope) return;
  await getQueryClient().fetchQuery(holidayCalendarSuggestionsQuery(service, scope));
}

/** Warm the signed-in user's own calendar connections for the Calendars loader. */
export async function prefetchMyCalendarSources(
  service: Pick<WorkspaceCalendarService, "listUserSources">,
): Promise<void> {
  const scope = scopeIfReady(GLOBAL_TENANT, "calendar sources");
  if (!scope) return;
  await getQueryClient().fetchQuery(myCalendarSourcesQuery(service, scope));
}

/** Warm the country and region catalogue for a loader. */
export async function prefetchHolidayRegions(
  service: Pick<WorkspaceCalendarService, "listHolidayRegions">,
): Promise<void> {
  const scope = scopeIfReady(GLOBAL_TENANT, "holiday regions");
  if (!scope) return;
  await getQueryClient().fetchQuery(holidayRegionsQuery(service, scope));
}

/** Warm a provider's domain-wide config (its first members page) for the Calendars loader. */
export async function prefetchCalendarConfig(
  service: Pick<
    WorkspaceCalendarService,
    "getGoogleDomainWideConfig" | "getOutlookDomainWideConfig"
  >,
  slug: string,
  provider: CalendarProvider,
): Promise<void> {
  const scope = scopeIfReady(slug, `${provider} calendar config`);
  if (!scope) return;
  await getQueryClient().fetchInfiniteQuery(calendarConfigQuery(service, scope, provider));
}
